import React, { useRef, useEffect, useState } from 'react';
import { useViewportObserver } from '@/hooks/useViewportObserver';

// Video asset imports
import abstractParticles from '@/assets/videos/abstract-particles.mp4';
import oceanWaves from '@/assets/videos/ocean-waves.mp4';
import forestMist from '@/assets/videos/forest-mist.mp4';
import digitalMatrix from '@/assets/videos/digital-matrix.mp4';
import cosmicNebula from '@/assets/videos/cosmic-nebula.mp4';
import flowingWater from '@/assets/videos/flowing-water.mp4';
import nightCity from '@/assets/videos/night-city.mp4';
import goldenSunset from '@/assets/videos/golden-sunset.mp4';
import auroraLights from '@/assets/videos/aurora-lights.mp4';
import fireFlames from '@/assets/videos/fire-flames.mp4';

// Legacy import for existing video-background
import lockBackground from '@/assets/lock-background.mp4';

interface VideoBackgroundProps {
  type: string;
  isPreview?: boolean;
  pauseAnimations?: boolean;
  className?: string;
}

// Video mapping
const videoAssets = {
  'video-background': lockBackground, // Legacy video
  'video-abstract-particles': abstractParticles,
  'video-ocean-waves': oceanWaves,
  'video-forest-mist': forestMist,
  'video-digital-matrix': digitalMatrix,
  'video-cosmic-nebula': cosmicNebula,
  'video-flowing-water': flowingWater,
  'video-night-city': nightCity,
  'video-golden-sunset': goldenSunset,
  'video-aurora-lights': auroraLights,
  'video-fire-flames': fireFlames,
};

export const VideoBackground: React.FC<VideoBackgroundProps> = ({ 
  type, 
  isPreview = false, 
  pauseAnimations = false,
  className = "" 
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  
  // Use viewport observer to pause video when out of view for performance
  const { ref: observerRef, isVisible, pauseAnimations: shouldPause } = useViewportObserver();

  const videoSrc = videoAssets[type as keyof typeof videoAssets];

  // Combine refs for viewport observer
  useEffect(() => {
    if (containerRef.current && observerRef) {
      (observerRef as React.MutableRefObject<HTMLElement>).current = containerRef.current;
    }
  }, [observerRef]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedData = () => setIsLoaded(true);
    const handleError = () => setHasError(true);

    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('error', handleError);

    return () => {
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('error', handleError);
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Pause/play based on viewport visibility and animations setting
    if (pauseAnimations || shouldPause || !isVisible) {
      video.pause();
    } else if (isLoaded && !hasError) {
      video.play().catch(() => {
        // Silently handle autoplay failures
        setHasError(true);
      });
    }
  }, [pauseAnimations, shouldPause, isVisible, isLoaded, hasError]);

  if (!videoSrc || hasError) {
    // Fallback to a simple gradient
    return (
    <div 
      ref={containerRef}
      className={`absolute inset-0 bg-gradient-to-br from-background/95 to-muted/50 ${className}`}
    />
    );
  }

  return (
    <div ref={containerRef} className={`absolute inset-0 overflow-hidden ${className}`}>
      <video
        ref={videoRef}
        src={videoSrc}
        autoPlay
        loop
        muted
        playsInline
        className={`w-full h-full object-cover ${
          isPreview ? 'scale-110' : ''
        } ${!isLoaded ? 'opacity-0' : 'opacity-100'} transition-opacity duration-700`}
        style={{
          filter: isPreview ? 'blur(0.5px)' : 'none'
        }}
      />
      
      {/* Loading state */}
      {!isLoaded && !hasError && (
        <div className="absolute inset-0 bg-gradient-to-br from-background/95 to-muted/50 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
};